import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ExtendedBaseTab } from '@common/classes/extended-base-tab';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { AttachmentService } from '@common/services/attachment.service';
import { marker } from '@common/translate-marker';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { FileDownloadService } from '@services/file-download.service';
import { PcsDocumentAndApprovalServices } from '@services/pcs-document-and-approval-services.service';
import { ToastService } from '@services/toast.service';
import { CustomExtendedTabService } from 'app/singleton-services/custom-extended-tab.service';
import { PcsDocumentAndApprovalActionStatusChangesDisplayModel } from '../../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/DetailsDisplayModels/PcsDocumentAndApprovalActionStatusChangesDisplayModel';
import { PcsDocumentAndApprovalAttachmentDisplayModel } from '../../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/DetailsDisplayModels/PcsDocumentAndApprovalAttachmentDisplayModel';

@Component({
    selector: 'app-pcs-approval-tab-attachments',
    templateUrl: './pcs-approval-tab-attachments.component.html',
    styleUrls: ['./pcs-approval-tab-attachments.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PcsApprovalTabAttachmentsComponent extends ExtendedBaseTab implements OnInit {
    attachments: PcsDocumentAndApprovalAttachmentDisplayModel[];

    @Input() pcsDocumentOrApprovalId = -1;

    @Input() isForApproval = false;

    @Input() currentDocumentStatus: CodelistDisplayModel;

    @Input() allowAttachmentConditions: {
        pcsDocumentAndApprovalAttachmentRole: CodelistDisplayModel;
        statusesWhereAttachmentIsAllowed: CodelistDisplayModel;
    }[];

    @Input() allowAttachment = false;

    @Input() isOnlyWatcher;

    @Input() createdByOrganizationId: number;

    @Input() actionStatusChanges: PcsDocumentAndApprovalActionStatusChangesDisplayModel[];

    @ViewChild('attachmentDescriptionTextArea') attachmentDescriptionTextArea: TextAreaComponent;

    skip = 0;

    pageSize = 10;

    gridView: GridDataResult;

    openedId = -1;

    newAttachmentDialog = false;

    removeAttachmentDialog = false;

    fileToSend: string = null;
    fileName: string = null;

    currentlyRemovingAttachment = -1;

    currentlyUploading = false;

    attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 5242880,
        allowedExtensions: ['.pdf', '.jpg', '.png', '.csv', '.xls', '.txt', '.xlsx', '.doc', '.docx', '.jpeg', '.gif']
    };

    constructor(
        customExtendedTabService: CustomExtendedTabService,
        private service: PcsDocumentAndApprovalServices,
        private fileDownloadService: FileDownloadService,
        private attachmentService: AttachmentService,
        private toastyService: ToastService
    ) {
        super(customExtendedTabService);
    }

    ngOnInit(): void {
        this.defaultPaging();
        this.loadAttachmentSettings();
        this.loadAttachments();
    }

    public openNewAttachmentDialog() {
        if (!this.currentlyUploading) {
            this.newAttachmentDialog = true;
        }
    }

    public closeNewAttachmentDialog() {
        if (!this.currentlyUploading) {
            this.fileToSend = null;
            this.fileName = null;
            this.newAttachmentDialog = false;
        }
    }

    public submitNewAttachment() {
        if (!this.currentlyUploading) {
            this.currentlyUploading = true;

            this.service
                .newAttachment({
                    isForApproval: this.isForApproval,
                    pcsDocumentOrApprovalId: this.pcsDocumentOrApprovalId,
                    fileContent: this.fileToSend,
                    fileName: this.fileName,
                    fileDescription: this.attachmentDescriptionTextArea.value
                })
                .subscribe({
                    next: (response) => {
                        this.currentlyUploading = false;
                        this.newAttachmentDialog = false;
                        this.loadAttachments();
                    },
                    error: () => {
                        this.currentlyUploading = false;
                    }
                });
        }
    }

    public openRemoveAttachmentDialog(attachmentId: number) {
        this.removeAttachmentDialog = true;
        this.currentlyRemovingAttachment = attachmentId;
    }

    public closeRemoveAttachmentDialog() {
        this.removeAttachmentDialog = false;
        this.currentlyRemovingAttachment = -1;
    }

    public submitRemoveAttachment() {
        this.service
            .removeAttachment({
                isForApproval: this.isForApproval,
                attachmentId: this.currentlyRemovingAttachment
            })
            .subscribe({
                next: (response) => {
                    this.closeRemoveAttachmentDialog();
                    this.loadAttachments();
                }
            });
    }

    public showAddButton() {
        if (this.user.isSuperAdmin) {
            return true;
        }

        if (!this.hasPermission('PcsApprovals.PcsApprovals.Edit')) {
            return false;
        }

        const userOrgId = this.user.organizationId;
        const matchingAacs = this.allowAttachmentConditions.filter(
            (aac2) => aac2.statusesWhereAttachmentIsAllowed.name === this.currentDocumentStatus.name
        );

        if (matchingAacs.length === 0) {
            return false;
        }

        const userOrgIdStr = userOrgId?.toString();
        const isCreatorOrg = this.createdByOrganizationId?.toString() === userOrgIdStr;
        const isInStatusChanges = this.actionStatusChanges?.some((sc) => sc.forOrganization?.id === userOrgIdStr);
        const hasAttachmentRole = matchingAacs.some((aac) =>
            this.userHasRole(aac.pcsDocumentAndApprovalAttachmentRole.name)
        );

        console.log(
            matchingAacs,
            this.currentDocumentStatus,
            isCreatorOrg,
            isInStatusChanges,
            this.actionStatusChanges
        );

        return isCreatorOrg || isInStatusChanges || hasAttachmentRole;
    }

    fileSelected($event) {
        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    this.translateService.instant(marker('Attachment size invalid')) + ' - ' + element.name
                );
                return;
            }
            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    this.translateService.instant(marker('Invalid attachment')) + ' - ' + element.name
                );
                return;
            }
            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    documentOnLoad(file: string, name: string) {
        this.fileToSend = file;
        this.fileName = name;
    }

    fileRemoved($event) {
        this.fileToSend = null;
        this.fileName = null;
    }

    attachmentDownload(id: number): void {
        this.service.downloadAttachment(id).subscribe((response) => {
            this.fileDownloadService.handleAttachmentDownload(response);
        });
    }

    private loadAttachments(): void {
        this.isBusy = true;
        this.service.getPcsApprovalAttachments(this.pcsDocumentOrApprovalId).subscribe((response) => {
            this.attachments = response;
            this.isBusy = false;
        });
    }

    private loadAttachmentSettings(): void {
        this.attachmentService.getAttachmentSettingsForUpload().subscribe((response) => {
            this.attachmentFileUploadRestrictions = response as FileRestrictions;
        });
    }
}
